/**
 * Per-frame render probe for the Debug → Perf readout (docs/PERFORMANCE.md).
 *
 * Brackets Phaser's step with `PRE_STEP` / `POST_RENDER` for main-thread ms, wraps the
 * live WebGL context's draw / upload / framebuffer calls to count work, and times the GPU
 * with `EXT_disjoint_timer_query` when the driver offers it. Everything is instance-level
 * and undone by `destroy()`; nothing here changes what is drawn.
 */
import * as Phaser from 'phaser';
import {
  summarizeFrames,
  type FrameSample,
  type PerfReadout,
} from '../../ui/play/perfReadout.ts';

const WINDOW_FRAMES = 90;
const MAX_PENDING_QUERIES = 8;

type Gl = WebGLRenderingContext | WebGL2RenderingContext;

type TimerExt = {
  begin: () => unknown;
  end: (query: unknown) => void;
  available: (query: unknown) => boolean;
  disjoint: () => boolean;
  resultNs: (query: unknown) => number;
  dispose: (query: unknown) => void;
};

type Wrapped = {
  name: string;
  original: (...args: unknown[]) => unknown;
  /** The context already had an own property; put it back instead of deleting. */
  own: boolean;
};

function timerExtension(gl: Gl): TimerExt | null {
  const gl2 = typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext;

  if (gl2) {
    const ext = gl.getExtension('EXT_disjoint_timer_query_webgl2') as
      | { TIME_ELAPSED_EXT: number; GPU_DISJOINT_EXT: number }
      | null;

    if (!ext) {
      return null;
    }

    return {
      begin: () => {
        const query = gl.createQuery();

        if (query) {
          gl.beginQuery(ext.TIME_ELAPSED_EXT, query);
        }

        return query;
      },
      end: () => gl.endQuery(ext.TIME_ELAPSED_EXT),
      available: (query) =>
        gl.getQueryParameter(query as WebGLQuery, gl.QUERY_RESULT_AVAILABLE) === true,
      disjoint: () => gl.getParameter(ext.GPU_DISJOINT_EXT) === true,
      resultNs: (query) => Number(gl.getQueryParameter(query as WebGLQuery, gl.QUERY_RESULT)),
      dispose: (query) => gl.deleteQuery(query as WebGLQuery),
    };
  }

  const ext = gl.getExtension('EXT_disjoint_timer_query') as
    | {
        TIME_ELAPSED_EXT: number;
        GPU_DISJOINT_EXT: number;
        QUERY_RESULT_AVAILABLE_EXT: number;
        QUERY_RESULT_EXT: number;
        createQueryEXT: () => unknown;
        beginQueryEXT: (target: number, query: unknown) => void;
        endQueryEXT: (target: number) => void;
        getQueryObjectEXT: (query: unknown, pname: number) => unknown;
        deleteQueryEXT: (query: unknown) => void;
      }
    | null;

  if (!ext) {
    return null;
  }

  return {
    begin: () => {
      const query = ext.createQueryEXT();
      ext.beginQueryEXT(ext.TIME_ELAPSED_EXT, query);
      return query;
    },
    end: () => ext.endQueryEXT(ext.TIME_ELAPSED_EXT),
    available: (query) => ext.getQueryObjectEXT(query, ext.QUERY_RESULT_AVAILABLE_EXT) === true,
    disjoint: () => gl.getParameter(ext.GPU_DISJOINT_EXT) === true,
    resultNs: (query) => Number(ext.getQueryObjectEXT(query, ext.QUERY_RESULT_EXT)),
    dispose: (query) => ext.deleteQueryEXT(query),
  };
}

export class FrameProbe {
  private readonly game: Phaser.Game;
  private readonly gl: Gl | null;
  private readonly timer: TimerExt | null;
  private readonly wrapped: Wrapped[] = [];
  private readonly frames: FrameSample[] = [];
  private readonly pending: { query: unknown; frame: FrameSample }[] = [];
  private stepStart = 0;
  private activeQuery: unknown = null;
  private counters = { drawCalls: 0, indices: 0, uploadBytes: 0, fboBinds: 0 };
  private destroyed = false;

  constructor(game: Phaser.Game) {
    this.game = game;
    const renderer = game.renderer as { type: number; gl?: Gl };
    this.gl = renderer.type === Phaser.WEBGL && renderer.gl ? renderer.gl : null;
    this.timer = this.gl ? timerExtension(this.gl) : null;

    if (this.gl) {
      this.wrapGl(this.gl);
    }

    game.events.on(Phaser.Core.Events.PRE_STEP, this.onPreStep, this);
    game.events.on(Phaser.Core.Events.POST_RENDER, this.onPostRender, this);
  }

  get hasGpuTimer(): boolean {
    return this.timer !== null;
  }

  /** Summary of the last ~90 frames. Cheap enough to call a few times a second. */
  readout(): PerfReadout {
    return summarizeFrames(this.frames, this.game.loop.actualFps);
  }

  destroy(): void {
    if (this.destroyed) {
      return;
    }

    this.destroyed = true;
    this.game.events.off(Phaser.Core.Events.PRE_STEP, this.onPreStep, this);
    this.game.events.off(Phaser.Core.Events.POST_RENDER, this.onPostRender, this);

    const target = this.gl as unknown as Record<string, unknown>;

    for (const { name, original, own } of this.wrapped) {
      if (own) {
        target[name] = original;
      } else {
        // We shadowed a prototype method with an own property; drop it to restore lookup.
        delete target[name];
      }
    }

    this.wrapped.length = 0;

    if (this.timer) {
      for (const entry of this.pending) {
        this.timer.dispose(entry.query);
      }
    }

    this.pending.length = 0;
    this.frames.length = 0;
  }

  private wrapGl(gl: Gl): void {
    const target = gl as unknown as Record<string, (...args: unknown[]) => unknown>;
    const counters = this.counters;
    const wrap = (
      name: string,
      count: (args: unknown[]) => void,
    ): void => {
      const original = target[name];

      if (typeof original !== 'function') {
        return;
      }

      this.wrapped.push({
        name,
        original,
        own: Object.prototype.hasOwnProperty.call(gl, name),
      });
      target[name] = function wrappedGl(this: unknown, ...args: unknown[]) {
        count(args);
        return original.apply(gl, args);
      };
    };

    wrap('drawElements', (args) => {
      counters.drawCalls += 1;
      counters.indices += Number(args[1]) || 0;
    });
    wrap('drawArrays', (args) => {
      counters.drawCalls += 1;
      counters.indices += Number(args[2]) || 0;
    });
    wrap('drawElementsInstanced', (args) => {
      counters.drawCalls += 1;
      counters.indices += (Number(args[1]) || 0) * (Number(args[4]) || 1);
    });
    wrap('drawArraysInstanced', (args) => {
      counters.drawCalls += 1;
      counters.indices += (Number(args[2]) || 0) * (Number(args[3]) || 1);
    });
    wrap('bufferSubData', (args) => {
      const data = args[2] as { byteLength?: number } | undefined;
      counters.uploadBytes += data?.byteLength ?? 0;
    });
    wrap('bufferData', (args) => {
      const data = args[1] as { byteLength?: number } | number | undefined;
      counters.uploadBytes +=
        typeof data === 'number' ? data : (data?.byteLength ?? 0);
    });
    wrap('bindFramebuffer', () => {
      counters.fboBinds += 1;
    });
  }

  private onPreStep(): void {
    this.collectQueries();
    this.counters.drawCalls = 0;
    this.counters.indices = 0;
    this.counters.uploadBytes = 0;
    this.counters.fboBinds = 0;
    this.stepStart = performance.now();

    if (this.timer && this.pending.length < MAX_PENDING_QUERIES) {
      this.activeQuery = this.timer.begin();
    }
  }

  private onPostRender(): void {
    const frame: FrameSample = {
      jsMs: performance.now() - this.stepStart,
      gpuMs: null,
      drawCalls: this.counters.drawCalls,
      triangles: Math.round(this.counters.indices / 3),
      uploadBytes: this.counters.uploadBytes,
      fboBinds: this.counters.fboBinds,
    };

    this.frames.push(frame);

    if (this.frames.length > WINDOW_FRAMES) {
      this.frames.shift();
    }

    if (this.timer && this.activeQuery) {
      this.timer.end(this.activeQuery);
      this.pending.push({ query: this.activeQuery, frame });
      this.activeQuery = null;
    }
  }

  private collectQueries(): void {
    const timer = this.timer;

    if (!timer || this.pending.length === 0) {
      return;
    }

    if (timer.disjoint()) {
      for (const entry of this.pending) {
        timer.dispose(entry.query);
      }

      this.pending.length = 0;
      return;
    }

    while (this.pending.length > 0) {
      const head = this.pending[0]!;

      if (!timer.available(head.query)) {
        break;
      }

      head.frame.gpuMs = timer.resultNs(head.query) / 1e6;
      timer.dispose(head.query);
      this.pending.shift();
    }
  }
}
